import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Loader2, Pause, Play, Trash2, Pencil, ArrowRight, Repeat, AlertTriangle } from "lucide-react";
import { FadeUp } from "./Reveal";
import {
  CADENCE_LABELS,
  formatDollars,
  formatWhen,
  useRecurringGifts,
} from "../lib/recurringGifts";

/**
 * RecurringGiftsCard — the "why sign in" surface for returning donors.
 *
 * Lists a donor's active/paused recurring gifts, lets them:
 *   - Pause / resume
 *   - Edit amount + cadence
 *   - Cancel (soft — status flips to cancelled server-side)
 *
 * Until Phase E (Stripe), gifts are captured in Mongo only. A subtle
 * mock badge reminds donors these won't charge until launch.
 */
const CADENCE_OPTS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annually" },
];

const StatusPill = ({ status }) => {
  const map = {
    active: { bg: "bg-grass/12", fg: "text-grass", label: "Active" },
    paused: { bg: "bg-yellow/20", fg: "text-ink", label: "Paused" },
    cancelled: { bg: "bg-border", fg: "text-olive", label: "Cancelled" },
  };
  const m = map[status] || map.paused;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full ${m.bg} px-3 py-1 text-[13px] font-semibold uppercase tracking-[0.18em] ${m.fg}`}>
      <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${status === "active" ? "bg-grass animate-pulse" : "bg-ink/30"}`} />
      {m.label}
    </span>
  );
};

const GiftRow = ({ gift, onUpdate, onCancel, busy }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    amount_cents: gift.amount_cents,
    cadence: gift.cadence,
  });

  const save = async () => {
    await onUpdate(gift.id, draft);
    setEditing(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
      className="rounded-xl border border-border bg-bone p-5"
      data-testid={`gift-row-${gift.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <StatusPill status={gift.status} />
            {gift.is_mock && (
              <span
                data-testid={`gift-mock-badge-${gift.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-tangerine/40 bg-tangerine/8 px-2.5 py-0.5 text-[13px] font-semibold uppercase tracking-[0.2em] text-tangerine"
                title="Charges begin once Stripe is wired up"
              >
                <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.4} />
                Pre-launch — no charges yet
              </span>
            )}
          </div>
          <p className="mt-3 font-serif text-3xl text-ink" data-testid={`gift-amount-${gift.id}`}>
            {formatDollars(gift.amount_cents)}
            <span className="ml-2 text-base text-olive">
              · {CADENCE_LABELS[gift.cadence] || gift.cadence}
            </span>
          </p>
          <p className="mt-2 text-[13px] text-olive">
            {gift.preference_label
              ? <>Preferring <strong className="text-ink">{gift.preference_label}</strong></>
              : "General fund"}
          </p>
          {gift.status === "active" && (
            <p className="mt-1 text-[13px] text-olive">
              Next scheduled charge: <span className="text-ink">{formatWhen(gift.next_charge_at)}</span>
            </p>
          )}
        </div>

        {gift.status !== "cancelled" && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing((v) => !v)}
              data-testid={`gift-edit-${gift.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-sand px-3.5 py-1.5 text-[13px] font-semibold text-ink transition-colors hover:border-ink"
            >
              <Pencil className="h-3 w-3" /> Edit
            </button>
            {gift.status === "active" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => onUpdate(gift.id, { status: "paused" })}
                data-testid={`gift-pause-${gift.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-sand px-3.5 py-1.5 text-[13px] font-semibold text-ink transition-colors hover:border-ink"
              >
                <Pause className="h-3 w-3" /> Pause
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => onUpdate(gift.id, { status: "active" })}
                data-testid={`gift-resume-${gift.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-sand px-3.5 py-1.5 text-[13px] font-semibold text-ink transition-colors hover:border-ink"
              >
                <Play className="h-3 w-3" /> Resume
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (typeof window !== "undefined" && !window.confirm("Cancel this recurring gift?")) return;
                onCancel(gift.id);
              }}
              data-testid={`gift-cancel-${gift.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-scarlet/30 bg-sand px-3.5 py-1.5 text-[13px] font-semibold text-scarlet transition-colors hover:bg-scarlet/8"
            >
              <Trash2 className="h-3 w-3" /> Cancel
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-5 grid grid-cols-1 gap-4 border-t border-border pt-5 sm:grid-cols-2">
              <label className="block">
                <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Amount</span>
                <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-sand px-3 py-2">
                  <span className="font-serif text-3xl text-olive">$</span>
                  <input
                    type="number"
                    min={1}
                    max={10000}
                    step={1}
                    value={draft.amount_cents / 100}
                    onChange={(e) => setDraft({ ...draft, amount_cents: Math.round(Number(e.target.value) * 100) })}
                    data-testid={`gift-edit-amount-${gift.id}`}
                    className="w-full bg-transparent font-serif text-3xl text-ink focus:outline-none"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Cadence</span>
                <select
                  value={draft.cadence}
                  onChange={(e) => setDraft({ ...draft, cadence: e.target.value })}
                  data-testid={`gift-edit-cadence-${gift.id}`}
                  className="mt-2 w-full rounded-md border border-border bg-sand px-3 py-2.5 font-serif text-3xl text-ink focus:border-ink focus:outline-none"
                >
                  {CADENCE_OPTS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>
              <div className="col-span-full flex gap-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={busy}
                  data-testid={`gift-save-${gift.id}`}
                  className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60"
                >
                  Save changes <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-full border border-border bg-sand px-5 py-2.5 text-base font-semibold text-ink"
                >
                  Cancel edit
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const NewGiftForm = ({ onCreate, busy, onDismiss }) => {
  const [amount, setAmount] = useState(25);
  const [cadence, setCadence] = useState("monthly");
  const [pref, setPref] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    await onCreate({
      amount_cents: Math.round(amount * 100),
      cadence,
      preference_label: pref.trim() || null,
    });
    onDismiss();
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border-2 border-tangerine bg-tangerine/[0.04] p-6"
      data-testid="new-gift-form"
    >
      <h3 className="font-serif text-3xl text-ink">Start a new recurring gift.</h3>
      <p className="mt-2 text-[13px] text-olive">
        Set an amount and how often. You can change or cancel it anytime.
      </p>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <label className="block">
          <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Amount ($)</span>
          <input
            type="number"
            min={1}
            max={10000}
            step={1}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            required
            data-testid="new-gift-amount"
            className="mt-2 w-full rounded-md border border-border bg-sand px-3 py-2.5 font-serif text-3xl text-ink focus:border-ink focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Cadence</span>
          <select
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
            data-testid="new-gift-cadence"
            className="mt-2 w-full rounded-md border border-border bg-sand px-3 py-2.5 font-serif text-3xl text-ink focus:border-ink focus:outline-none"
          >
            {CADENCE_OPTS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
            Preference (optional)
          </span>
          <input
            type="text"
            value={pref}
            onChange={(e) => setPref(e.target.value)}
            placeholder="e.g. Maya R. — Stanford"
            data-testid="new-gift-preference"
            className="mt-2 w-full rounded-md border border-border bg-sand px-3 py-2.5 font-serif text-3xl text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none"
          />
        </label>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={busy}
          data-testid="new-gift-submit"
          className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat className="h-4 w-4" />}
          Start recurring gift
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-full border border-border bg-sand px-5 py-3 text-base font-semibold text-ink"
        >
          Not right now
        </button>
      </div>
    </form>
  );
};

export const RecurringGiftsCard = ({ compact = false, canCreate = true }) => {
  const { gifts, loading, error, create, update, cancel } = useRecurringGifts();
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const wrap = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  return (
    <section data-testid="recurring-gifts-card" className="rounded-2xl border-2 border-ink/10 bg-bone p-6 lg:p-8">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
            Recurring gifts
          </span>
          <h2 className="mt-2 font-serif text-3xl text-ink">Your ongoing support.</h2>
        </div>
        {compact && (
          <button
            type="button"
            onClick={() => navigate("/recurring-gifts")}
            data-testid="rg-manage-link"
            className="inline-flex items-center gap-1.5 text-base font-semibold text-ink underline underline-offset-4 hover:text-tangerine"
          >
            Manage <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {error && (
        <p className="mt-4 rounded-md bg-scarlet/8 px-3 py-2 text-[13px] text-scarlet">
          {error}
        </p>
      )}

      {loading ? (
        <div className="mt-6 flex items-center gap-3 text-olive">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading your gifts…
        </div>
      ) : gifts.length === 0 ? (
        <FadeUp>
          <div className="mt-6 rounded-xl border border-dashed border-border bg-sand p-6" data-testid="rg-empty">
            <p className="font-serif text-3xl text-ink">No recurring gifts yet.</p>
            <p className="mt-2 max-w-md text-base leading-relaxed text-olive">
              Recurring donors are the reason a student can plan two semesters ahead instead of one. Start with any amount, any cadence — change it or stop any time.
            </p>
            {canCreate && !formOpen && (
              <button
                type="button"
                onClick={() => setFormOpen(true)}
                data-testid="rg-start-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-base font-semibold text-sand transition-colors hover:bg-forest"
              >
                <Repeat className="h-4 w-4" /> Start a recurring gift
              </button>
            )}
          </div>
        </FadeUp>
      ) : (
        <div className="mt-6 space-y-4">
          <AnimatePresence>
            {gifts.slice(0, compact ? 2 : gifts.length).map((g) => (
              <GiftRow
                key={g.id}
                gift={g}
                busy={busy}
                onUpdate={(id, patch) => wrap(() => update(id, patch))}
                onCancel={(id) => wrap(() => cancel(id))}
              />
            ))}
          </AnimatePresence>
          {!compact && canCreate && !formOpen && (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              data-testid="rg-add-another"
              className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-border bg-sand px-5 py-3 text-base font-semibold text-ink transition-colors hover:border-ink"
            >
              <Repeat className="h-4 w-4" /> Add another recurring gift
            </button>
          )}
        </div>
      )}

      {!compact && canCreate && formOpen && (
        <div className="mt-6">
          <NewGiftForm
            busy={busy}
            onCreate={(body) => wrap(() => create(body))}
            onDismiss={() => setFormOpen(false)}
          />
        </div>
      )}
    </section>
  );
};
